import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["video", "slide", "sheet", "scrollContainer", "playButton", "muteButton", "sharePanel", "shareBackdrop", "shareLink", "copyLabel", "progressBar", "progressTrack", "progressFill", "progressThumb"]
  static values = { from: String, backUrl: String }

  connect() {
    this.boundUnlockBody = this.unlockBody.bind(this)
    document.addEventListener("turbo:before-cache", this.boundUnlockBody)

    if (this.isMobile()) {
      this.lockBody()
    }

    this.globalMuted = true
    this.wheelLocked = false
    this.isSeeking = false
    this.progressLoopId = null
    this.trackWidth = 0

    this.resizeHandler = this.updateTrackWidth.bind(this)
    window.addEventListener("resize", this.resizeHandler)
    this.updateTrackWidth()

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      root: this.hasScrollContainerTarget ? this.scrollContainerTarget : this.element,
      threshold: 0.6
    })

    this.slideTargets.forEach((slide) => this.observer.observe(slide))

    this.keyHandler = this.handleKey.bind(this)
    document.addEventListener("keydown", this.keyHandler)

    this.wheelHandler = this.handleWheel.bind(this)
    if (!this.isMobile() && this.hasScrollContainerTarget) {
      this.scrollContainerTarget.addEventListener("wheel", this.wheelHandler, { passive: false })
    }

    this.preloadSecondSlide()
    this.scrollToFromPost()
  }

  disconnect() {
    this.observer.disconnect()
    document.removeEventListener("keydown", this.keyHandler)
    document.removeEventListener("turbo:before-cache", this.boundUnlockBody)
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.removeEventListener("wheel", this.wheelHandler)
    }
    window.removeEventListener("resize", this.resizeHandler)
    this.stopProgressLoop()
    this.unlockBody()
  }

  lockBody() {
    document.body.style.overflow = "hidden"
  }

  unlockBody() {
    document.body.style.overflow = ""
  }

  isMobile() {
    return window.innerWidth < 800
  }

  scrollToFromPost() {
    if (!this.hasFromValue) return

    const targetSlide = this.slideTargets.find((slide) => slide.dataset.postId === this.fromValue)
    if (targetSlide) {
      setTimeout(() => {
        targetSlide.scrollIntoView({ behavior: "auto", block: "start" })
      }, 50)
    }
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      const video = entry.target.querySelector("video")
      if (!video) return

      if (entry.isIntersecting) {
        this.ensureVideoSource(video)
        this.preloadNextSlide(entry.target)
        video.currentTime = 0
        video.muted = this.globalMuted
        this.updateMuteButton(video)
        this.updatePlayButton(video)
        this.resetProgressBar(entry.target)
        video.play().catch(() => {})
      } else {
        video.pause()
        video.currentTime = 0
        this.stopProgressLoop()
        this.resetProgressBar(entry.target)
      }
    })
  }

  setVideoSource(video) {
    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src
      video.removeAttribute("data-src")
      return true
    }
    return false
  }

  ensureVideoSource(video) {
    if (this.setVideoSource(video)) {
      video.load()
    }
  }

  preloadNextSlide(currentSlide) {
    const nextSlide = currentSlide.nextElementSibling
    if (!nextSlide) return

    const video = nextSlide.querySelector("video")
    if (!video) return

    this.setVideoSource(video)
  }

  preloadSecondSlide() {
    const secondSlide = this.slideTargets[1]
    if (!secondSlide) return

    const video = secondSlide.querySelector("video")
    if (!video) return

    this.setVideoSource(video)
  }

  togglePlay(event) {
    const video = event.currentTarget
    if (video.paused) {
      video.play().catch(() => {})
    } else {
      video.pause()
    }
  }

  playClick(event) {
    event.stopPropagation()
    const slide = event.currentTarget.closest("[data-clips-target='slide']")
    if (!slide) return

    const video = slide.querySelector("video")
    if (video && video.paused) video.play().catch(() => {})
  }

  handlePlay(event) {
    const video = event.currentTarget
    this.updatePlayButton(video)
    this.startProgressLoop(video)
  }

  handlePause(event) {
    const video = event.currentTarget
    this.updatePlayButton(video)
    this.stopProgressLoop()
  }

  updatePlayButton(video) {
    const slide = video.closest("[data-clips-target='slide']")
    if (!slide) return

    const button = slide.querySelector("[data-clips-target='playButton']")
    if (!button) return

    button.classList.toggle("hidden", !video.paused)
  }

  updateTrackWidth() {
    const track = this.progressTrackTargets[0]
    if (track) this.trackWidth = track.offsetWidth
  }

  resetProgressBar(slide) {
    const fill = slide.querySelector("[data-clips-target='progressFill']")
    const thumb = slide.querySelector("[data-clips-target='progressThumb']")
    if (fill) fill.style.transform = "scaleX(0)"
    if (thumb) thumb.style.transform = "translateX(-6px) translateY(-50%)"
  }

  startProgressLoop(video) {
    this.stopProgressLoop()
    this.updateTrackWidth()
    this.activeProgressVideo = video
    this.progressLoop(video)
  }

  stopProgressLoop() {
    this.activeProgressVideo = null
    if (this.progressLoopId && typeof this.progressLoopId === "number") {
      cancelAnimationFrame(this.progressLoopId)
    }
    this.progressLoopId = null
  }

  progressLoop(video) {
    if (video !== this.activeProgressVideo || video.paused || video.ended) return

    if (!this.isSeeking) {
      this.updateProgressBar(video)
    }

    if (typeof video.requestVideoFrameCallback === "function") {
      this.progressLoopId = video.requestVideoFrameCallback(() => this.progressLoop(video))
    } else {
      this.progressLoopId = requestAnimationFrame(() => this.progressLoop(video))
    }
  }

  isSlideActive(slide) {
    const rect = slide.getBoundingClientRect()
    return rect.top >= -rect.height * 0.4 && rect.top < rect.height * 0.6
  }

  updateProgressBar(video) {
    const slide = video.closest("[data-clips-target='slide']")
    if (!slide) return

    const fill = slide.querySelector("[data-clips-target='progressFill']")
    const thumb = slide.querySelector("[data-clips-target='progressThumb']")
    if (!fill || !thumb) return

    if (!this.trackWidth) this.updateTrackWidth()
    if (!this.trackWidth) return

    const duration = video.duration || 0
    const f = duration > 0 ? video.currentTime / duration : 0
    fill.style.transform = `scaleX(${f})`

    const thumbHalf = 6
    const x = Math.max(thumbHalf, Math.min(this.trackWidth - thumbHalf, f * this.trackWidth))
    thumb.style.transform = `translateX(${x - thumbHalf}px) translateY(-50%)`
  }

  seekStart(event) {
    event.preventDefault()
    event.stopPropagation()

    const bar = event.currentTarget
    bar.setPointerCapture(event.pointerId)
    this.isSeeking = true
    this.seekTo(event)
  }

  seekMove(event) {
    if (!this.isSeeking) return

    event.preventDefault()
    event.stopPropagation()
    this.seekTo(event)
  }

  seekEnd(event) {
    event.preventDefault()
    event.stopPropagation()

    const bar = event.currentTarget
    if (bar.hasPointerCapture(event.pointerId)) {
      bar.releasePointerCapture(event.pointerId)
    }

    this.seekTo(event)
    this.isSeeking = false
  }

  seekTo(event) {
    const bar = event.currentTarget
    const slide = bar.closest("[data-clips-target='slide']")
    if (!slide) return

    const video = slide.querySelector("video")
    if (!video || !video.duration) return

    const rect = bar.getBoundingClientRect()
    const x = event.clientX - rect.left
    const pct = Math.max(0, Math.min(1, x / rect.width)) * 100
    video.currentTime = (pct / 100) * video.duration
    this.updateProgressBar(video)
  }

  toggleMute(event) {
    event.stopPropagation()
    const activeSlide = this.activeSlide()
    if (!activeSlide) return

    const video = activeSlide.querySelector("video")
    if (!video) return

    this.globalMuted = !this.globalMuted
    video.muted = this.globalMuted
    this.updateMuteButton(video)
  }

  updateMuteButton(video) {
    const slide = video.closest("[data-clips-target='slide']")
    if (!slide) return

    const button = slide.querySelector("[data-clips-target='muteButton']")
    if (!button) return

    const onIcon = button.querySelector(".mute-on")
    const offIcon = button.querySelector(".mute-off")

    if (onIcon) onIcon.classList.toggle("hidden", video.muted)
    if (offIcon) offIcon.classList.toggle("hidden", !video.muted)
  }

  setShareUrl(event) {
    const url = event.currentTarget.dataset.shareUrl
    if (!url) return

    this.currentShareUrl = url

    this.shareLinkTargets.forEach((link) => {
      const provider = link.dataset.shareProvider
      const encoded = encodeURIComponent(url)
      let shareUrl = "#"

      switch (provider) {
        case "max":
          shareUrl = `https://max.ru/share?text=${encoded}`
          break
        case "vk":
          shareUrl = `https://vk.com/share.php?url=${encoded}`
          break
        case "telegram":
          shareUrl = `https://t.me/share/url?url=${encoded}`
          break
        case "whatsapp":
          shareUrl = `https://wa.me/?text=${encoded}`
          break
        case "ok":
          shareUrl = `https://connect.ok.ru/offer?url=${encoded}`
          break
      }

      link.href = shareUrl
    })

    if (this.hasCopyLabelTarget) {
      this.copyLabelTarget.textContent = "Скопировать ссылку"
    }
  }

  copyShareUrl(event) {
    event.stopPropagation()
    const url = this.currentShareUrl
    if (!url) return

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).catch(() => {})
    } else {
      const textarea = document.createElement("textarea")
      textarea.value = url
      textarea.setAttribute("readonly", "")
      textarea.style.position = "fixed"
      textarea.style.left = "-9999px"
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand("copy")
      } catch (e) {
        // ignore
      }
      document.body.removeChild(textarea)
    }

    if (this.hasCopyLabelTarget) {
      const original = this.copyLabelTarget.textContent
      this.copyLabelTarget.textContent = "Скопировано ✓"
      clearTimeout(this.copyLabelResetTimeout)
      this.copyLabelResetTimeout = setTimeout(() => {
        this.copyLabelTarget.textContent = original
      }, 1500)
    }
  }

  openComments(event) {
    event.stopPropagation()
    const slide = event.currentTarget.closest("[data-clips-target='slide']")
    if (!slide) return

    const sheet = slide.querySelector("[data-clips-target='sheet']")
    if (sheet) sheet.classList.remove("translate-y-full")
  }

  closeComments(event) {
    event.stopPropagation()
    const sheet = event.currentTarget.closest("[data-clips-target='sheet']")
    if (sheet) sheet.classList.add("translate-y-full")
  }

  closeShareSheet() {
    if (this.hasSharePanelTarget) this.sharePanelTarget.classList.add("hidden")
    if (this.hasShareBackdropTarget) this.shareBackdropTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  handleKey(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.nextSlide()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.prevSlide()
    } else if (event.key === "Escape") {
      event.preventDefault()

      if (this.hasSharePanelTarget && !this.sharePanelTarget.classList.contains("hidden")) {
        this.closeShareSheet()
        return
      }

      const openSheet = this.sheetTargets.find((sheet) => !sheet.classList.contains("translate-y-full"))
      if (openSheet) {
        openSheet.classList.add("translate-y-full")
      } else if (this.hasBackUrlValue) {
        window.location.href = this.backUrlValue
      }
    }
  }

  nextSlide() {
    const active = this.activeSlide()
    if (!active) return

    const next = active.nextElementSibling
    if (next && next.dataset.clipsTarget === "slide") {
      next.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  prevSlide() {
    const active = this.activeSlide()
    if (!active) return

    const prev = active.previousElementSibling
    if (prev && prev.dataset.clipsTarget === "slide") {
      prev.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  activeSlide() {
    return this.slideTargets.find((slide) => {
      const rect = slide.getBoundingClientRect()
      return rect.top >= -rect.height * 0.4 && rect.top < rect.height * 0.6
    })
  }

  handleWheel(event) {
    if (event.ctrlKey || event.metaKey) return

    const deltaY = event.deltaY
    if (Math.abs(deltaY) < 1) return

    event.preventDefault()

    if (this.wheelLocked) return

    const container = this.scrollContainerTarget
    const slideHeight = container.clientHeight
    const currentIndex = Math.round(container.scrollTop / slideHeight)
    const direction = Math.sign(deltaY)
    const maxIndex = this.slideTargets.length - 1
    let nextIndex = currentIndex + direction

    if (nextIndex < 0) nextIndex = 0
    if (nextIndex > maxIndex) nextIndex = maxIndex
    if (nextIndex === currentIndex) return

    this.wheelLocked = true
    container.scrollTo({ top: nextIndex * slideHeight, behavior: "smooth" })

    const unlock = () => {
      this.wheelLocked = false
      container.removeEventListener("scrollend", unlock)
    }
    container.addEventListener("scrollend", unlock)
    setTimeout(unlock, 400)
  }
}
